// Auto-declarative registry of every orchestratable visualizer, replacing
// orchestrator.js's old hard-coded LIBRARY array. The failure mode that
// array had was silent: a complete, working visualizer could sit outside
// the rotation with nothing to flag it. This file makes that failure loud
// instead: every candidate module is imported here, and any of them
// missing its own exported PROFILE gets a console.error at startup, not
// silence.
//
// Why static imports and not a real directory scan: this bundles through
// esbuild for the browser (see package.json's build:party-mode script) -
// there is no fs.readdir at runtime, imports are resolved at build time.
// A literal "list every file in visualizers/" is not something a browser
// bundle can do; a fixed, explicit import list is the closest equivalent,
// and PROFILE-per-module is what keeps a forgotten entry from disappearing
// into that fixed list unnoticed.
//
// Not every file in visualizers/ is a candidate. Deliberately excluded, all
// pre-existing decisions from before this registry existed, not new ones:
//   geometry.js      - pulled from rotation ("not impressive"), parked for
//                       a possible later static/artwork-driven visualizer.
//   placeholder.js    - dev-only pipeline validation cube, never a candidate.
//   plasma-field.js    - a real, complete visualizer, but absent from the
//                       brief's own PROFILE table.
//   rig-test.js       - steps 4/5's validation harness, explicitly "no
//                       longer wired in" per main.js's own comment.
//   neon-pulse.js     - the 'fallback' delegate adaptive-party.js falls
//                       back to when track data is unavailable, not a
//                       scored rotation candidate itself.
//   tunnel.js         - pulled from rotation on request; the module and
//                       its adaptive-party.js composition-building code
//                       are left intact, just not offered here.
import * as dustLayer from './particle-layers/dust-layer.js';
import * as stormLayer from './particle-layers/storm-layer.js';
import * as neonParticles from './visualizers/neon-particles.js';
import * as neonGrid from './visualizers/neon-grid.js';
import * as spotWall from './visualizers/spot-wall.js';
import * as discoSquares from './visualizers/disco-squares.js';
import * as daftPyramid from './visualizers/daft-pyramid.js';
import * as kineticRig from './visualizers/kinetic-rig.js';
import * as laserCathedral from './visualizers/laser-cathedral.js';
import * as laserGrid from './visualizers/laser-grid.js';
import * as mainStage from './visualizers/main-stage.js';
import * as pulseCore from './visualizers/pulse-core.js';
import * as kaleidoDrift from './visualizers/kaleido-drift.js';
import * as drosteDive from './visualizers/droste-dive.js';
import * as chromeFlux from './visualizers/chrome-flux.js';
import * as vinylGroove from './visualizers/vinyl-groove.js';
import * as phosphorScope from './visualizers/phosphor-scope.js';
import * as constellation from './visualizers/constellation.js';
import * as typeSlam from './visualizers/type-slam.js';
import * as particleStorm from './visualizers/particle-storm.js';
import * as resonance from './visualizers/resonance.js';
import * as halftone from './visualizers/halftone.js';
import * as sumi from './visualizers/sumi.js';
import * as caustics from './visualizers/caustics.js';
import * as loom from './visualizers/loom.js';
import * as aurora from './visualizers/aurora.js';
import * as frost from './visualizers/frost.js';
import * as reed from './visualizers/reed.js';
import * as anamorph from './visualizers/anamorph.js';
import * as portal from './visualizers/portal.js';
import * as echo from './visualizers/echo.js';
import * as preset from './visualizers/preset.js';
import * as silk from './visualizers/silk.js';
import * as shatter from './visualizers/shatter.js';
import * as swarm from './visualizers/swarm.js';
import * as nebula from './visualizers/nebula.js';
import * as splitflap from './visualizers/splitflap.js';
import * as gallery from './visualizers/gallery.js';
import * as shoji from './visualizers/shoji.js';
import * as sand from './visualizers/sand.js';
import * as prism from './visualizers/prism.js';
import * as ocean from './visualizers/ocean.js';
import * as turing from './visualizers/turing.js';
import * as pyro from './visualizers/pyro.js';
import * as cascade from './visualizers/cascade.js';
import * as mosh from './visualizers/mosh.js';
import * as strobewall from './visualizers/strobewall.js';
import * as beamwall from './visualizers/beamwall.js';
import * as kelp from './visualizers/kelp.js';
import * as poster from './visualizers/poster.js';
import * as liquidlight from './visualizers/liquidlight.js';
import * as kineticrig2 from './visualizers/kinetic-rig2.js';
import * as steam from './visualizers/steam.js';
import * as focus from './visualizers/focus.js';
import * as infinity from './visualizers/infinity.js';
import * as ember from './visualizers/ember.js';
import * as lichtenberg from './visualizers/lichtenberg.js';
import * as origami from './visualizers/origami.js';
import * as portals from './visualizers/portals.js';
import * as advect from './visualizers/advect.js';
import * as ravesign from './visualizers/ravesign.js';
import * as airframe from './visualizers/airframe.js';
import * as maelstrom from './visualizers/maelstrom.js';
import * as nightdrive from './visualizers/nightdrive.js';
import * as kaleido from './visualizers/kaleido.js';
import * as tessel from './visualizers/tessel.js';
import * as vertigo from './visualizers/vertigo.js';
import * as lantern from './visualizers/lantern.js';
import * as scope3d from './visualizers/scope3d.js';
import * as countdown from './visualizers/countdown.js';
import * as fireflies from './visualizers/fireflies.js';
import * as hex from './visualizers/hex.js';
import * as metronome from './visualizers/metronome.js';
import * as rosace from './visualizers/rosace.js';
import * as incense from './visualizers/incense.js';
import * as loxodrome from './visualizers/loxodrome.js';
import * as hyperbolic from './visualizers/hyperbolic.js';
import * as tessera from './visualizers/tessera.js';
import * as blueprint from './visualizers/blueprint.js';
import * as bayfront from './visualizers/bayfront.js';
import * as signal from './visualizers/signal.js';
import * as lighthouse from './visualizers/lighthouse.js';
import * as polytope from './visualizers/polytope.js';
import * as cycle from './visualizers/cycle.js';
import * as thermal from './visualizers/thermal.js';
import * as warehouse from './visualizers/warehouse.js';
import * as chladni from './visualizers/chladni.js';
import * as jellyfish from './visualizers/jellyfish.js';
import * as pendula from './visualizers/pendula.js';
import * as texer from './visualizers/texer.js';
import * as outrun from './visualizers/outrun.js';
import * as aspen from './visualizers/aspen.js';
import * as mirrorarray from './visualizers/mirrorarray.js';
import * as neonalley from './visualizers/neonalley.js';
import * as underground from './visualizers/underground.js';
import * as frames from './visualizers/frames.js';
import * as flame from './visualizers/flame.js';
import * as koi from './visualizers/koi.js';
import * as tesla from './visualizers/tesla.js';
import * as pulsar from './visualizers/pulsar.js';
import * as skyline from './visualizers/skyline.js';
import * as arcade from './visualizers/arcade.js';
import * as bloom from './visualizers/bloom.js';
import * as lightpaint from './visualizers/lightpaint.js';
import * as kintsugi from './visualizers/kintsugi.js';
import * as harp from './visualizers/harp.js';
import * as fountain from './visualizers/fountain.js';
import * as startrails from './visualizers/startrails.js';
import * as glowtide from './visualizers/glowtide.js';
import * as pinball from './visualizers/pinball.js';
import * as lightcycles from './visualizers/lightcycles.js';
import * as hyperloop from './visualizers/hyperloop.js';
import * as mandala from './visualizers/mandala.js';
import * as eclipse from './visualizers/eclipse.js';
import * as pane from './visualizers/pane.js';
import * as splitflap2 from './visualizers/splitflap2.js';
import * as headliner from './visualizers/headliner.js';
import * as hangar from './visualizers/hangar.js';
import * as goldenhour from './visualizers/goldenhour.js';
import * as hyperspace from './visualizers/hyperspace.js';
import * as dancefloor from './visualizers/dancefloor.js';
import * as mixdeck from './visualizers/mixdeck.js';
import * as typeclash from './visualizers/typeclash.js';
import * as domino from './visualizers/domino.js';
import * as twist from './visualizers/twist.js';
import * as jukebox from './visualizers/jukebox.js';
import * as pipes from './visualizers/pipes.js';
import * as gyro from './visualizers/gyro.js';
import * as slitscan from './visualizers/slitscan.js';
import * as julia from './visualizers/julia.js';
import * as blackhole from './visualizers/blackhole.js';
import * as mycelium from './visualizers/mycelium.js';
import * as penrose from './visualizers/penrose.js';
import * as chromeflow from './visualizers/chromeflow.js';
import * as dissolve from './visualizers/dissolve.js';
import * as glass from './visualizers/glass.js';
import * as glyphs from './visualizers/glyphs.js';
import * as bubbles from './visualizers/bubbles.js';
import * as bauhaus from './visualizers/bauhaus.js';
import * as metro from './visualizers/metro.js';
import * as zoetrope from './visualizers/zoetrope.js';
import * as morph from './visualizers/morph.js';
import * as branches from './visualizers/branches.js';
import * as mobius from './visualizers/mobius.js';
import * as doorways from './visualizers/doorways.js';
import * as cymatics from './visualizers/cymatics.js';
import * as leviathan from './visualizers/leviathan.js';
import * as rings from './visualizers/rings.js';
import * as poincare from './visualizers/poincare.js';
import * as oilshow from './visualizers/oilshow.js';
import * as kinetic from './visualizers/kinetic.js';
import * as haze from './visualizers/haze.js';
import * as borealis from './visualizers/borealis.js';
import * as ledcube from './visualizers/ledcube.js';
import * as ferro from './visualizers/ferro.js';
import * as fractal from './visualizers/fractal.js';
import * as poolside from './visualizers/poolside.js';
import * as magneto from './visualizers/magneto.js';
import * as acid from './visualizers/acid.js';
import * as cyberflight from './visualizers/cyberflight.js';
import * as maze from './visualizers/maze.js';
import * as sunburst from './visualizers/sunburst.js';
import * as wavefield from './visualizers/wavefield.js';
import * as contours from './visualizers/contours.js';
import * as seasons from './visualizers/seasons.js';
import * as isocubes from './visualizers/isocubes.js';
import * as datamosh from './visualizers/datamosh.js';
import * as tvwall from './visualizers/tvwall.js';
import * as comic from './visualizers/comic.js';
import * as blacklight from './visualizers/blacklight.js';
import * as cells from './visualizers/cells.js';
import * as pixelsort from './visualizers/pixelsort.js';
import * as videoloop from './visualizers/videoloop.js';
import * as galaxy from './visualizers/galaxy.js';
import * as moire from './visualizers/moire.js';
import * as formation from './visualizers/formation.js';
import * as facets from './visualizers/facets.js';
import * as pyromusical from './visualizers/pyromusical.js';
import * as thunderhead from './visualizers/thunderhead.js';
import * as cloudsea from './visualizers/cloudsea.js';
import * as shapes from './visualizers/shapes.js';
import * as stadium from './visualizers/stadium.js';
import * as rainwindow from './visualizers/rainwindow.js';
import * as hifi from './visualizers/hifi.js';
import * as woofer from './visualizers/woofer.js';
import * as panels from './visualizers/panels.js';
import * as sunroom from './visualizers/sunroom.js';
import * as matrix from './visualizers/matrix.js';
import * as fairground from './visualizers/fairground.js';
import * as halo from './visualizers/halo.js';
import * as pinscreen from './visualizers/pinscreen.js';
import * as moonlake from './visualizers/moonlake.js';
import * as balloons from './visualizers/balloons.js';
import * as dandelion from './visualizers/dandelion.js';
import * as truchet from './visualizers/truchet.js';
import * as murmuration from './visualizers/murmuration.js';
import * as neonsign2 from './visualizers/neonsign2.js';
import * as bokeh from './visualizers/bokeh.js';
import * as hourglass from './visualizers/hourglass.js';
import * as bubble from './visualizers/bubble.js';
import * as ballroom from './visualizers/ballroom.js';
import * as flowmap from './visualizers/flowmap.js';
import * as totem from './visualizers/totem.js';
import * as ribbons from './visualizers/ribbons.js';
import * as shockwave from './visualizers/shockwave.js';
import * as ink from './visualizers/ink.js';
import * as drones from './visualizers/drones.js';

const IMPORTED_MODULES = [
  dustLayer, stormLayer, neonParticles, neonGrid,
  spotWall, discoSquares, daftPyramid, kineticRig, laserCathedral, laserGrid, mainStage,
  pulseCore, kaleidoDrift, drosteDive, chromeFlux, vinylGroove,
  phosphorScope, constellation, typeSlam, particleStorm, resonance, sumi, halftone,
  loom,
  caustics,
  aurora,
  gallery,
  shoji,
  sand,
  prism,
  frost,
  reed, anamorph, portal, echo, preset, silk, swarm, nebula, ocean, turing, pyro, cascade, mosh, strobewall, beamwall, kelp, poster, focus, shatter, infinity, ember, lichtenberg, origami, portals, advect, ravesign, airframe, liquidlight, kineticrig2, steam, maelstrom, vertigo, nightdrive, kaleido, tessel, lantern, scope3d, countdown, fireflies, hex, metronome, rosace, incense, loxodrome, hyperbolic, tessera, blueprint, bayfront, signal, lighthouse, polytope, cycle, thermal, warehouse, chladni, jellyfish, pendula, texer, outrun, aspen, ferro, fractal, poolside, magneto, acid, cyberflight, maze, sunburst, wavefield, contours, seasons, isocubes, datamosh, tvwall, comic, blacklight, cells, pixelsort, videoloop, galaxy, moire, formation, facets, pyromusical, thunderhead, cloudsea, shapes, stadium, rainwindow, hifi, woofer, mirrorarray, neonalley, underground, frames, flame, koi, tesla, pulsar, skyline, arcade, bloom, lightpaint, kintsugi, harp, fountain, startrails, glowtide, pinball, lightcycles, hyperloop, mandala, eclipse, pane, splitflap2, headliner, hangar, goldenhour, hyperspace, dancefloor, mixdeck, typeclash, domino, twist, jukebox, pipes, gyro, slitscan, julia, blackhole, mycelium, penrose, chromeflow, dissolve, glass, glyphs, bubbles, bauhaus, metro, zoetrope, morph, branches, mobius, doorways, cymatics, leviathan, rings, poincare, oilshow, kinetic, haze, borealis, ledcube, panels, bubble, ballroom, flowmap, totem, ribbons, shockwave, ink, drones,
  sunroom,
  matrix,
  fairground,
  halo,
  pinscreen,
  moonlake,
  balloons,
  dandelion,
  truchet,
  murmuration,
  neonsign2,
  bokeh,
  hourglass,
  splitflap
];

// mirrorball: kept as a known id per the brief ("garde son entree, ne la
// supprime pas") even though its own visualizer module is broken/missing -
// warned about below rather than silently absent, but never imported (a
// broken module has no business being on the import graph).
const KNOWN_MISSING_IDS = ['mirrorball'];

function buildCandidates() {
  const candidates = [];
  for (const mod of IMPORTED_MODULES) {
    if (!mod.PROFILE) {
      // eslint-disable-next-line no-console
      console.error(
        '[visualizer-registry] module imported without an exported PROFILE - ' +
        'it will not appear in the rotation at all. Fix by exporting PROFILE ' +
        '(see any other visualizer module for the shape).'
      );
      continue;
    }
    candidates.push(mod.PROFILE);
  }
  for (const id of KNOWN_MISSING_IDS) {
    // eslint-disable-next-line no-console
    console.warn('[visualizer-registry] "' + id + '" has no visualizer module yet - skipped, not an error.');
  }
  return candidates;
}

// Built once at module load - PROFILE objects are static per-module
// constants, there is nothing to recompute per frame.
export const CANDIDATES = buildCandidates();

// OFFERTES À LA MAIN. Ces compositions apparaissent TOUJOURS dans la console
// VDJ, comme « Écran noir », qu'elles soient ou non dans le vivier du morceau
// en cours — et cliquer dessus les joue même si l'orchestrateur ne les aurait
// pas proposées.
//
// C'est une entorse assumée au principe de pickComposition (« on n'atteint
// jamais un visuel que l'orchestrateur n'aurait pas pris lui-même »), et elle
// ne vaut que pour ce qui reste HONNÊTE hors de sa porte d'éligibilité :
// countdown sans analyse ne compte pas vers un drop inconnu — cette branche
// est gardée par la timeline dans le visualiseur lui-même — il compte vers la
// FIN DU MORCEAU, ce qui ne demande que la durée et ne promet rien de faux.
// Un visualiseur qui mentirait hors de sa porte n'a rien à faire ici.
export const OFFERTES_A_LA_MAIN = ['countdown'];

export function getCandidate(id) {
  return CANDIDATES.find(function (c) { return c.id === id; }) || null;
}
